import apiClient from "../services/apiClient";
import { findExistingClient } from "./Utility";
import { updateDoc } from "firebase/firestore";
import JSZip from "jszip";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const formatKeyToUrl = (key) => {
  return `${BASE_URL}/files/${encodeURIComponent(key)}`;
};

export function resizeImageToPNG(
  file,
  { maxWidth = 300, maxHeight = 300 } = {}
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      // calculate target dimensions
      const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const targetW = Math.round(img.width * ratio);
      const targetH = Math.round(img.height * ratio);

      // draw into canvas
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, targetW, targetH);

      // convert to PNG blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return reject(new Error("Canvas is empty"));
          }

          const pngName = `formattedName.png`;
          resolve(new File([blob], pngName, { type: "image/png" }));
        },
        "image/png",
        0.9
      );
    };

    img.onerror = (err) => reject(err);
  });
}

/**
 * Map your uploadType string → resize constraints
 */
function getResizeOptions(type) {
  switch (type) {
    case "profile": //profile image / dp
    case "qr": // business qr
    case "bLogo": // business logo
      return { maxWidth: 300, maxHeight: 300 };

    case "bLogoLarge":
      return { maxWidth: 500, maxHeight: 500 };

    case "post":
      // only constrain width, let height scale
      return { maxWidth: 600, maxHeight: 600 };

    default:
      // no resizing
      return null;
  }
}

export const uploadCloudApi = async (file, userId, type, variant = null) => {
  try {
    // 1) Normalize to a File/Blob
    let fileToUpload;
    if (typeof file === "string") {
      // fetch remote URL → Blob
      const response = await fetch(file);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch image from URL: ${response.statusText}`
        );
      }
      const blob = await response.blob();
      fileToUpload = blob;
    } else if (file instanceof Blob || file instanceof File) {
      fileToUpload = file;
    } else {
      throw new Error("`file` must be a URL string, Blob, or File");
    }

    // 2) resize (and convert to PNG)
    if (variant) {
      const opts = getResizeOptions(variant);
      if (opts) {
        fileToUpload = await resizeImageToPNG(fileToUpload, opts);
      }
    }

    // 3) Derive extension & build filename
    const mime = fileToUpload.type; // e.g. "image/png"
    const ext = mime.split("/")[1] || "bin";

    let filename;
    if (variant === "profile") {
      filename = `UP-avatar-${userId}.${ext}`;
    } else {
      // timestamp + random to guarantee uniqueness
      filename = `UP-${type}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}.${ext}`;
    }

    // 4) Wrap in a new File
    const uploadFile = new File([fileToUpload], filename, {
      type: fileToUpload.type,
    });

    // 5) Build FormData & send
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("uploadType", type);
    formData.append("userId", userId);

    const { data } = await apiClient.post("/api/upload", formData);
    if (!data.fileUrl) {
      throw new Error("Upload failed: no fileUrl returned");
    }

    // 6) Return formatted URL
    return formatKeyToUrl(data.fileUrl);
  } catch (err) {
    console.error("Error uploading file:", err);
    throw err;
  }
};

// formatting old images pointing to new cloudflare storage
export const formatOldUrl = (url) => {
  if (!url || typeof url !== "string") {
    return url; // or return null, "" or some default if you prefer
  }

  const oldBase = process.env.REACT_APP_API_OLD_BASE_URL?.replace(/\/$/, "");

  if (!oldBase) {
    console.warn("Missing REACT_APP_API_OLD_BASE_URL");
    return url;
  }

  if (url.startsWith(oldBase + "/")) {
    const key = url.slice(oldBase.length + 1);
    const newKey = `migrated-files/${key}`;
    return formatKeyToUrl(newKey);
  }

  return url;
};

// ─── 2) Delete a file by its public URL ─────────────────────────────────────────────────
export const deleteCloudApi = async (url, userId) => {
  try {
    // 2a) Extract the storage key from the public URL
    //     e.g. if url = `${BASE_URL}/files/<someKey>`, remove `${BASE_URL}/files/`
    const rawKey = url.replace(`${BASE_URL}/files/`, "");
    const key = decodeURIComponent(rawKey);

    // 2b) Use apiClient.delete(...) with a JSON body
    await apiClient.delete("/api/delete", {
      data: {
        key: key,
        userId: userId,
      },
    });

    // console.log(`Successfully deleted file with key "${key}"`);
  } catch (error) {
    console.error("Failed to delete file:", error);
    throw error;
  }
};

export const downloadCloudApi = async (
  invoice,
  userData,
  invoiceClients,
  invoiceType
) => {
  try {
    const rawKey = invoice.pdfUrl.replace(`${BASE_URL}/files/`, "");
    const key = decodeURIComponent(rawKey);

    const curClient = findExistingClient(invoice.to, invoiceClients);
    const tempCid = curClient?.cid || null;

    const cid = tempCid ? tempCid : "CID000XX";
    const tempType = invoiceType === "noc" ? "" : "SQ-";
    const filename = `${cid}_${tempType}${invoice.invoiceNo}.pdf`;

    const userId = userData.uid;

    const response = await apiClient.post(
      "/api/download",
      { key, userId },
      { responseType: "blob" }
    );

    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    return { blob, filename };
  } catch (error) {
    console.error("Failed to download file:", error);
    throw error;
  }
};

export async function downloadAndZipCloudApi(
  invoices,
  userData,
  setProgress,
  invoiceClients,
  invoiceType,
  privateKey
) {
  const zip = new JSZip();

  // 1) Parallel downloads with decryption
  const downloads = invoices.map(async (inv) => {
    // First decrypt the invoice
    const decryptedInv = await decryptData(inv, userData.uid, privateKey);

    // Then download using the decrypted invoice
    return downloadCloudApi(
      decryptedInv,
      userData,
      invoiceClients,
      invoiceType
    );
  });

  const results = await Promise.all(downloads);

  // 2) Add to ZIP
  results.forEach(({ blob, filename }) => zip.file(filename, blob));

  // 3) Generate with progress callback
  const zipBlob = await zip.generateAsync({ type: "blob" }, (meta) => {
    // e.g. map 0–100% to 80–100% of your UI progress
    const pct = 80 + (meta.percent / 100) * 20;
    setProgress(pct);
  });

  // 4) Download
  const now = new Date();

  const formattedDate = [
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
    String(now.getFullYear()).slice(-2),
  ].join("-");

  const zipName =
    invoiceType === "noc"
      ? `Notice_of_Charges_${formattedDate}`
      : `Invoices_${formattedDate}`;

  const zipUrl = URL.createObjectURL(zipBlob);
  const a = document.createElement("a");
  a.href = zipUrl;
  a.download = zipName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(zipUrl);
}

// -------> Locally  DECRYPT <-------
const decryptBulkData = async (symKey, ciphertextBase64, ivBase64) => {
  const ciphertextBuffer = base64ToArrayBuffer(ciphertextBase64);
  const ivBuffer = base64ToArrayBuffer(ivBase64);
  const iv = new Uint8Array(ivBuffer);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    symKey,
    ciphertextBuffer
  );

  const decoder = new TextDecoder();
  const decryptedString = decoder.decode(decryptedBuffer);
  return JSON.parse(decryptedString);
};

export const hybridDecryptSymmetricKey = async (
  wrappedKeyBase64,
  ivBase64,
  ephemeralPublicKeyBase64,
  recipientPrivateKey
) => {
  // Import the ephemeral public key.
  const ephemeralKeyBuffer = base64ToArrayBuffer(ephemeralPublicKeyBase64);
  const ephemeralPublicKey = await window.crypto.subtle.importKey(
    "spki",
    ephemeralKeyBuffer,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    []
  );

  // Derive the shared AES-GCM key using ECDH with the recipient's private key and the ephemeral public key.
  const sharedKey = await window.crypto.subtle.deriveKey(
    {
      name: "ECDH",
      public: ephemeralPublicKey,
    },
    recipientPrivateKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );

  // Convert the wrapped key and IV from Base64.
  const wrappedKeyBuffer = base64ToArrayBuffer(wrappedKeyBase64);
  const ivBuffer = base64ToArrayBuffer(ivBase64);
  const iv = new Uint8Array(ivBuffer);

  // Decrypt (unwrap) the symmetric key raw bits.
  const symmetricKeyRaw = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    sharedKey,
    wrappedKeyBuffer
  );

  // Import the raw symmetric key as a CryptoKey.
  const symmetricKey = await window.crypto.subtle.importKey(
    "raw",
    symmetricKeyRaw,
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"]
  );

  return symmetricKey;
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const keyToArrayBuffer = (key) => {
  // Check if the key contains PEM header/footer.
  if (key.includes("-----BEGIN")) {
    // Remove the header, footer, and newlines.
    const b64 = key
      .replace(/-----BEGIN PRIVATE KEY-----/, "")
      .replace(/-----END PRIVATE KEY-----/, "")
      .replace(/\s/g, "");
    return base64ToArrayBuffer(b64);
  } else {
    // It's plain Base64.
    return base64ToArrayBuffer(key);
  }
};

const importRecipientPrivateKey = async (key) => {
  const binaryDer = keyToArrayBuffer(key);
  return await window.crypto.subtle.importKey(
    "pkcs8",
    binaryDer,
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    ["deriveKey"]
  );
};

export async function decryptBulkDataForRecipient(
  encryptedBulkData,
  recipientPrivateKey,
  recipientLabel
) {
  const { ciphertext, iv, ephemeralPublicKey, wrappedKeys } = encryptedBulkData;
  let symmetricKey;

  // Enforce that recipientPrivateKey must be present to attempt any decryption
  if (!recipientPrivateKey) {
    throw new Error("Recipient private key is required for decryption");
  }

  // Check if wrapped key exists for this recipient
  if (!wrappedKeys[recipientLabel]) {
    throw new Error(
      `No wrapped key found for recipient: ${recipientLabel}. Access denied.`
    );
  }

  try {
    // Recipient has their own wrapped key – decrypt using recipient's private key.
    const importedPrivateKey = await importRecipientPrivateKey(
      recipientPrivateKey
    );
    const { wrappedKey, iv: wrappedIv } = wrappedKeys[recipientLabel];
    symmetricKey = await hybridDecryptSymmetricKey(
      wrappedKey,
      wrappedIv,
      ephemeralPublicKey,
      importedPrivateKey
    );

    const decryptedData = await decryptBulkData(symmetricKey, ciphertext, iv);
    return { decryptedData, usedServerFallback: false };
  } catch (error) {
    console.warn(
      "Client-side decryption failed, falling back to server:",
      error
    );
    // Fallback: Call API to decrypt using admin key
    // Only reaches here if wrappedKeys[recipientLabel] exists but client-side decryption failed
    const decryptedData = await decryptApi(encryptedBulkData);
    return { decryptedData, usedServerFallback: true };
  }
}

export const decryptData = async (
  encrypted,
  uid,
  privateKey,
  recipients = null,
  docRef = null
) => {
  // console.log("DECRYPTED LOCALLY");
  if (encrypted?.encryptedData && privateKey && uid) {
    try {
      const { decryptedData, usedServerFallback } =
        await decryptBulkDataForRecipient(
          encrypted.encryptedData,
          privateKey,
          uid
        );
      // Remove the encryptedData property and merge in the decrypted data.
      const { encryptedData, ...rest } = encrypted;
      const result = { ...rest, ...decryptedData };

      // Re-encrypt with user's public key if we used server fallback
      if (usedServerFallback && docRef && recipients) {
        // console.log("Re-encrypting data!");
        // Don't await - run in background
        reEncryptAndSave(decryptedData, rest, recipients, docRef).catch(
          (error) => {
            console.error("Background re-encryption failed:", error);
          }
        );
      }

      return result;
    } catch (error) {
      console.error("Failed to decrypt:", error);
      throw error;
    }
  }
  return encrypted;
};

async function reEncryptAndSave(
  decryptedData,
  nonEncryptedData,
  recipients,
  docRef
) {
  try {
    // Encrypt the remaining data
    const newEncryptedData = await encryptApi(decryptedData, recipients);

    // Merge
    const updatedData = {
      ...nonEncryptedData,
      ...newEncryptedData,
    };

    // Update Firestore with the enhanced encryption
    await updateDoc(docRef, {
      encryptedData: updatedData,
    });

    // console.log("Re-encryption success.");
  } catch (error) {
    console.error("Failed to re-encrypt and save:", error);
    throw error;
  }
}

// ─── Encrypt ──────────────
export const encryptApi = async (dataObj, recipients) => {
  // console.log(dataObj, recipients);
  if (dataObj && recipients) {
    try {
      // console.log("TRYING TO ENCRYPT");

      const response = await apiClient.post("/api/encryption/encrypt", {
        dataObj: dataObj,
        recipients: recipients,
      });

      // console.log(response.data);

      return response.data.data;
    } catch (error) {
      console.error("Failed to encrypt:", error);
      throw error;
    }
  }
  return;
};

// ─── Decrypt ──────────────
export const decryptApi = async (encrypted) => {
  // console.log("decrypting via api");
  if (encrypted) {
    try {
      // console.log('TRYING TO DECRYPT');

      const response = await apiClient.post("/api/encryption/decrypt", {
        encryptedBulkData: encrypted,
      });

      // console.log(response.data);
      return response.data.data;
    } catch (error) {
      console.error("Failed to decrypt:", error);
      throw error;
    }
  }
  return encrypted;
};
